import {
  Box,
  Circle,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { CheckIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import { TFunction } from "i18next";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import * as Yup from "yup";
import { Routes } from "../../helpers/router";
import { useSendResetPasswordMutation } from "../../graphql/generated/user-hooks";
import { Button } from "../../components/chackra-ui-button";
import * as Sentry from "@sentry/react";

type FormValues = {
  email: string;
};

const validationSchema = (t: TFunction) =>
  Yup.object({
    email: Yup.string().required(t("resetPassword.emailRequired")),
  });

export const ResetPasswordForm = () => {
  const { t } = useTranslation();
  const [alertMessage, setAlertMessage] = useState<"completed" | undefined>(
    undefined
  );
  const toast = useToast();
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    watch,
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema(t)),
  });

  const [resetPassword] = useSendResetPasswordMutation({
    context: { endpoint: "fitnext" },
    onCompleted: () => setAlertMessage("completed"),
    onError: (err) => {
      toast({
        title: t("resetPassword.errorToast.title"),
        description: t("resetPassword.errorToast.description"),
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      Sentry.captureException(err);
    },
  });

  const onSubmit = handleSubmit(
    (values) => resetPassword({ variables: { email: values.email } }),
    (errors, event) => {
      Sentry.captureException(new Error("Unable to submit reset password form"), {
        extra: { event, errors },
      });
    }
  );

  const email = watch("email");

  if (alertMessage === "completed") {
    return (
      <VStack w="full" flex={1} justify="center" align="center" spacing={5}>
        <Circle size="56px" bg="green.50">
          <Icon boxSize={6} color="green.500">
            <CheckIcon />
          </Icon>
        </Circle>
        <Text
          fontFamily="'Barlow Condensed', sans-serif"
          fontSize="24px"
          fontWeight={700}
          color="#13192E"
          textAlign="center"
        >
          {t("resetPassword.successAlert.title")}
        </Text>

        <Box
          bg="slate.50"
          borderRadius="12px"
          px={5}
          py={3}
          textAlign="center"
        >
          <Text fontSize="14px" color="slate.500" mb={1}>
            {t("resetPassword.successAlert.sentTo", "Sent to")}
          </Text>
          <Text
            fontFamily="'Barlow Semi Condensed', sans-serif"
            fontSize="17px"
            fontWeight={600}
            color="#13192E"
          >
            {email}
          </Text>
        </Box>

        <Text fontSize="16px" color="slate.500" textAlign="center" maxW="400px" lineHeight="1.5">
          <Trans
            i18nKey="resetPassword.successAlert.description"
            components={{ bold: <Text as="span" fontWeight="bold" color="#13192E" /> }}
            values={{ email }}
          />
        </Text>

        <Button
          as={RouterLink}
          to={Routes.Login()}
          variant="senseiFilled"
          w="full"
          maxW="400px"
        >
          {t("login.email.loginButton")}
        </Button>

        <Text fontSize="14px" color="slate.400" textAlign="center" maxW="400px">
          {t("resetPassword.successAlert.spamWarning")}
        </Text>
      </VStack>
    );
  }

  return (
    <VStack w="full" flex={1} justify="center" spacing={0}>
      <VStack w="full" maxW="440px" spacing={0} px={4}>
        <Text
          fontFamily="'Barlow Condensed', sans-serif"
          fontSize="28px"
          fontWeight={700}
          color="#13192E"
          textAlign="center"
          mb="6px"
        >
          {t("resetPassword.title")}
        </Text>
        <Text
          fontSize="16px"
          color="slate.500"
          textAlign="center"
          mb={7}
        >
          {t("resetPassword.description")}
        </Text>

        <form onSubmit={onSubmit} autoComplete="off" style={{ width: "100%" }}>
          <VStack spacing={4} w="full">
            <FormControl isInvalid={!!errors.email}>
              <FormLabel fontSize="16px" fontWeight={600} color="slate.600">
                {t("resetPassword.emailLabel")}
              </FormLabel>
              <Input
                id="email"
                type="email"
                autoComplete="off"
                placeholder={t("login.email.emailPlaceholder", "you@email.com")}
                {...register("email")}
              />
              {errors.email?.message && (
                <FormErrorMessage>{errors.email.message}</FormErrorMessage>
              )}
            </FormControl>

            <Button
              type="submit"
              variant="senseiFilled"
              w="full"
              isLoading={isSubmitting}
              mt={3}
            >
              {t("resetPassword.submitButton")}
            </Button>
          </VStack>
        </form>
      </VStack>
    </VStack>
  );
};
